import React, { useState, useEffect } from 'react';
import { Trophy, Medal, Award, TrendingUp, ArrowLeft } from 'lucide-react';
import { getLeaderboard, getUserRank } from './firebase/leaderboardService';
import { getUserProfile } from './firebase/authService';

const Leaderboard = ({ currentUser, onNavigate, onGoBack, onSignOut }) => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [userRank, setUserRank] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [userXP, setUserXP] = useState(0); // Store XP from progress document
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadLeaderboard = async () => {
    setLoading(true);
    setError('');

    try {
      const result = await getLeaderboard(100);
      
      if (result.success) {
        // Debug: log leaderboard data
        console.log('Leaderboard data:', result.leaderboard);
        if (result.leaderboard.length > 0) {
          console.log('First user example:', result.leaderboard[0]);
        }
        
        setLeaderboard(result.leaderboard);
        
        // Get current user's rank and profile if logged in
        if (currentUser) {
          try {
            // Get user's profile and progress from Firestore
            const profileResult = await getUserProfile(currentUser.uid);
            if (profileResult.success) {
              setUserProfile(profileResult.profile);
              
              // Get user's XP from progress document (source of truth)
              const { getUserProgress } = await import('./firebase/progressService');
              const progressResult = await getUserProgress(currentUser.uid);
              const firestoreXP = progressResult.success && progressResult.progress ? 
                (progressResult.progress.totalXP || 0) : 0;
              
              setUserXP(firestoreXP); // Store XP for display
              
              const rankResult = await getUserRank(currentUser.uid, firestoreXP);
              if (rankResult.success) {
                setUserRank(rankResult.rank);
              }
            }
          } catch (error) {
            console.error('Error loading user data:', error);
          }
        }
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLeaderboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy className="w-6 h-6 text-yellow-500" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-gray-400" />;
    if (rank === 3) return <Medal className="w-6 h-6 text-orange-600" />;
    return <span className="text-gray-600 font-bold">#{rank}</span>;
  };

  const getCountryFlag = (country) => {
    // Debug: log the country value
    if (!country) {
      console.warn('No country provided to getCountryFlag');
      return '🌍';
    }
    
    // Emoji flags for all supported countries
    const flags = {
      'United States': '🇺🇸',
      'Canada': '🇨🇦',
      'United Kingdom': '🇬🇧',
      'Australia': '🇦🇺',
      'Germany': '🇩🇪',
      'France': '🇫🇷',
      'Spain': '🇪🇸',
      'Italy': '🇮🇹',
      'Brazil': '🇧🇷',
      'Mexico': '🇲🇽',
      'Argentina': '🇦🇷',
      'India': '🇮🇳',
      'China': '🇨🇳',
      'Japan': '🇯🇵',
      'South Korea': '🇰🇷',
      'Philippines': '🇵🇭',
      'Indonesia': '🇮🇩',
      'Nigeria': '🇳🇬',
      'South Africa': '🇿🇦',
      'Kenya': '🇰🇪',
      'Other': '🌍'
    };
    
    // Return flag or fallback to globe if country not found
    const flag = flags[country];
    if (!flag) {
      console.warn(`No flag found for country: "${country}"`);
      return '🌍';
    }
    return flag;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 pb-20">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          {onGoBack && (
            <button
              onClick={onGoBack}
              className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-slate-200 rounded-xl hover:border-blue-500 hover:text-blue-600 transition mb-4 font-semibold"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
          )}

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-black text-gray-900 mb-2">Global Leaderboard</h1>
              <p className="text-lg text-gray-600">Top learners from around the world</p>
            </div>
          </div>
        </div>

        {/* User's Rank Card */}
        {currentUser && userRank && (
          <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl p-6 text-white mb-6 shadow-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="bg-white/20 p-3 rounded-xl">
                  <div className="text-4xl">{userProfile?.country && getCountryFlag(userProfile.country)}</div>
                </div>
                <div>
                  <p className="text-blue-100 text-sm font-medium">
                    {userProfile?.firstName || currentUser.displayName?.split(' ')[0] || 'Your'} Rank
                  </p>
                  <p className="text-3xl font-black">#{userRank}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-blue-100 text-sm font-medium">Total XP</p>
                <p className="text-3xl font-black">{userXP}</p>
              </div>
            </div>
          </div>
        )}

        {/* Leaderboard */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6">
            <div className="flex items-center gap-3 text-white">
              <TrendingUp className="w-6 h-6" />
              <h2 className="text-2xl font-black">Top 100 Leaders</h2>
            </div>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
              <p className="text-gray-600 mt-4">Loading leaderboard...</p>
            </div>
          ) : error ? (
            <div className="p-12 text-center">
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={loadLeaderboard}
                className="px-6 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition font-semibold"
              >
                Try Again
              </button>
            </div>
          ) : leaderboard.length === 0 ? (
            <div className="p-12 text-center text-gray-600">
              <Award className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-semibold">Be the first on the leaderboard!</p>
              <p className="text-sm">Complete lessons to earn XP and claim your spot</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {leaderboard.map((user, index) => {
                const rank = index + 1;
                const isCurrentUser = currentUser && user.uid === currentUser.uid;
                
                // Debug: log each user
                if (isCurrentUser) {
                  console.log('Current user on leaderboard:', user);
                  console.log('- firstName:', user.firstName);
                  console.log('- lastName:', user.lastName);
                  console.log('- name:', user.name);
                  console.log('- country:', user.country);
                  console.log('- All fields:', Object.keys(user));
                }
                
                return (
                  <div
                    key={user.uid}
                    className={`p-4 flex items-center gap-4 transition ${
                      isCurrentUser ? 'bg-blue-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    {/* Rank */}
                    <div className="w-12 flex justify-center">
                      {getRankIcon(rank)}
                    </div>

                    {/* User Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl" role="img" aria-label={user.country || 'Country'}>
                          {user.country ? getCountryFlag(user.country) : '🌍'}
                        </span>
                        <p className={`font-bold text-lg ${isCurrentUser ? 'text-blue-600' : 'text-gray-900'}`}>
                          {user.firstName || user.name || (isCurrentUser && currentUser?.displayName) || 'User'} {isCurrentUser && <span className="text-sm font-normal">(You)</span>}
                        </p>
                      </div>
                      <p className="text-sm text-gray-500 ml-9">{user.country || 'Unknown'}</p>
                    </div>

                    {/* XP */}
                    <div className="text-right">
                      <p className="text-2xl font-black text-amber-600">{user.totalXP}</p>
                      <p className="text-xs text-gray-500">XP</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Not signed in message */}
        {!currentUser && (
          <div className="mt-6 bg-blue-50 border-2 border-blue-200 rounded-2xl p-6 text-center">
            <Trophy className="w-12 h-12 text-blue-600 mx-auto mb-3" />
            <p className="text-lg font-bold text-gray-900 mb-2">Want to join the leaderboard?</p>
            <p className="text-gray-600 mb-4">Create an account to track your progress and compete globally</p>
            <button
              onClick={() => onNavigate('home')}
              className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl font-bold hover:from-blue-600 hover:to-purple-700 transition shadow-lg"
            >
              Sign Up Now
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Leaderboard;
